import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { createColor, getColors, updateColor } from '@/features/operations/api';
import type { ColorItem } from '@/features/operations/types';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

/// Names the operator is editing, keyed by colour id. Held as typed so a half-finished edit is
/// never saved on a keystroke.
type NameDrafts = Record<string, string>;

function buildNameDrafts(colors: ColorItem[]): NameDrafts {
  return colors.reduce<NameDrafts>((drafts, color) => {
    drafts[color.id] = color.name;
    return drafts;
  }, {});
}

function countLabel(count: number, singular: string, plural: string) {
  return count === 1 ? `1 ${singular}` : `${count} ${plural}`;
}

/**
 * Says what deactivating this colour does before the operator does it: it stops being offered,
 * and nothing that already has it changes (#186).
 */
function describeConsequence(color: ColorItem) {
  const models = countLabel(color.phoneModelCount, 'modelo', 'modelos');
  const devices = countLabel(color.deviceCount, 'equipo', 'equipos');

  if (!color.isActive) {
    if (color.phoneModelCount === 0 && color.deviceCount === 0) {
      return 'No se ofrece y nada lo usa. Al reactivarlo se puede asignar a los modelos.';
    }

    return color.phoneModelCount === 0
      ? `No se ofrece. Lo conservan ${devices}.`
      : `No se ofrece. Lo conservan ${models} y ${devices}; al reactivarlo vuelve a ofrecerse en esos modelos.`;
  }

  if (color.phoneModelCount === 0) {
    return 'Todavía no está asignado a ningún modelo, así que no se ofrece en la recepción.';
  }

  return `Lo tienen ${models} y ${devices}. Si lo desactivás deja de ofrecerse, pero no cambia nada de lo que ya lo tiene.`;
}

/**
 * The general list of colours (#186, BR-014). iPhone repeats colours across models, so each
 * colour is created once here and then assigned to every model that has it, from the models
 * screen.
 */
export function AdminColorsPage() {
  const { token } = useAuth();

  const [colors, setColors] = useState<ColorItem[]>([]);
  const [nameDrafts, setNameDrafts] = useState<NameDrafts>({});
  const [newColorName, setNewColorName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const loaded = await getColors(token);
        setColors(loaded);
        setNameDrafts(buildNameDrafts(loaded));
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'No pudimos cargar los colores.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [token]);

  if (!token) {
    return null;
  }

  const refresh = async () => {
    const loaded = await getColors(token);
    setColors(loaded);
    setNameDrafts(buildNameDrafts(loaded));
  };

  const run = async (action: () => Promise<void>, successText: string) => {
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await action();
      setSuccessMessage(successText);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'No pudimos guardar los cambios.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = () =>
    run(async () => {
      if (!newColorName.trim()) {
        throw new Error('Poné el nombre del color.');
      }

      await createColor(token, { name: newColorName.trim() });
      setNewColorName('');
      await refresh();
    }, 'Color creado. Asignalo a los modelos que lo tienen desde Modelos del catálogo.');

  const handleRename = (color: ColorItem) =>
    run(async () => {
      const name = (nameDrafts[color.id] || '').trim();

      if (!name) {
        throw new Error('El nombre no puede quedar vacío.');
      }

      if (name === color.name) {
        throw new Error('El nombre no cambió.');
      }

      await updateColor(token, color.id, { name });
      await refresh();
    }, 'Color renombrado.');

  const handleToggleActive = (color: ColorItem) =>
    run(
      async () => {
        await updateColor(token, color.id, { isActive: !color.isActive });
        await refresh();
      },
      color.isActive ? 'Color desactivado.' : 'Color reactivado.',
    );

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="CONFIGURACIÓN COMERCIAL"
        title="Colores"
        description="La lista general de colores. Cada color se carga una sola vez y se asigna a los modelos que lo tienen; en la recepción se elige entre los colores del modelo."
        badge="Solo administración"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <>
          <SectionCard
            title="Agregar un color"
            description="Se crea activo y sin modelos. El nombre no puede repetirse, aunque cambien mayúsculas."
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  placeholder="Medianoche"
                  value={newColorName}
                  onChange={(event) => setNewColorName(event.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <PrimaryButton onClick={handleCreate} disabled={isSaving}>
                  Agregar color
                </PrimaryButton>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="Colores registrados"
            description="Los colores no se borran: se desactivan, para que los equipos que los tienen sigan diciendo de qué color son."
          >
            {colors.length === 0 ? (
              <Alert severity="info">
                Todavía no hay colores. Sin colores asignados a un modelo, la recepción no puede
                registrar el color de sus equipos.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {colors.map((color, index) => (
                  <Box key={color.id}>
                    {index > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={`Nombre de ${color.name}`}
                          value={nameDrafts[color.id] ?? ''}
                          onChange={(event) =>
                            setNameDrafts((drafts) => ({
                              ...drafts,
                              [color.id]: event.target.value,
                            }))
                          }
                          disabled={isSaving}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={color.isActive ? 'Se ofrece' : 'Inactivo'}
                            color={color.isActive ? 'success' : 'default'}
                            variant={color.isActive ? 'filled' : 'outlined'}
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {countLabel(color.phoneModelCount, 'modelo', 'modelos')} ·{' '}
                            {countLabel(color.deviceCount, 'equipo', 'equipos')}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <PrimaryButton
                            size="small"
                            onClick={() => handleRename(color)}
                            disabled={isSaving}
                          >
                            Guardar nombre
                          </PrimaryButton>
                          <PrimaryButton
                            size="small"
                            color="secondary"
                            onClick={() => handleToggleActive(color)}
                            disabled={isSaving}
                          >
                            {color.isActive ? 'Desactivar' : 'Reactivar'}
                          </PrimaryButton>
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {describeConsequence(color)} Última modificación:{' '}
                          {new Date(color.updatedAt).toLocaleString('es-UY')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Los colores de cada modelo se eligen en{' '}
              <Link component={RouterLink} to="/admin/phone-models">
                Modelos del catálogo
              </Link>
              .
            </Typography>
          </SectionCard>
        </>
      )}
    </Stack>
  );
}
